import { Link } from "react-router-dom";
import { RotateCcw, ShieldCheck, ShieldAlert, Check, ArrowRight } from "lucide-react";
import { MaskedLines } from "@/components/MaskedLines";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";

export default function Policies() {
  const { t } = useLang();

  const sections = [
    { icon: RotateCcw, title: t("policies.retTitle"), points: t("policies.retPoints"), accent: "text-sky-400", testid: "return-policy" },
    { icon: ShieldCheck, title: t("policies.garTitle"), points: t("policies.garPoints"), accent: "text-emerald-400", testid: "warranty-policy" },
    { icon: ShieldAlert, title: t("policies.safetyTitle"), points: t("checkout.safety"), accent: "text-amber-400", testid: "safety-policy" },
  ];

  return (
    <main className="mx-auto max-w-5xl px-4 pt-32 pb-24 sm:px-8">
      <Seo title={`${t("policies.l1")} ${t("policies.l2")} | NordiGuard`} description={t("policies.sub")} />

      <header className="pt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("policies.eyebrow")}</p>
        <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
          <MaskedLines lines={[t("policies.l1"), t("policies.l2")]} />
        </h1>
        <p className="mt-4 max-w-xl text-base text-slate-400">{t("policies.sub")}</p>
      </header>

      <div className="mt-12 space-y-6">
        {sections.map((s, i) => (
          <Reveal key={s.testid} delay={i * 0.08}>
            <section data-testid={s.testid} className="glacial-card p-8">
              <div className="flex items-center gap-3">
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 ${s.accent}`}>
                  <s.icon size={21} />
                </span>
                <h2 className="font-display text-xl font-bold text-slate-50">{s.title}</h2>
              </div>
              <ul className="mt-5 space-y-3">
                {s.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sky-400">
                      <Check size={12} />
                    </span>
                    {pt}
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-12 text-center">
        <Link
          to="/contact"
          data-testid="policies-contact-cta"
          className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-3.5 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_26px_rgba(56,189,248,0.45)]"
        >
          {t("policies.cta")}
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </main>
  );
}
